import httpChatbot from '@/api/httpChatbot'
import store from '@/store'
import { chatbotBaseUrl } from './backend'

// Parse one SSE frame ("event: foo\ndata: {...}", one or more data: lines) into
// { event, data }. Returns null for a frame with no data line (e.g. a stray
// keep-alive) so the caller can just skip it.
function parseSseFrame (frame) {
  let event = 'message'
  const dataLines = []
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (!dataLines.length) return null
  return { event, data: JSON.parse(dataLines.join('\n')) }
}

// Sends one chat turn and streams its progress. `onStatus(label)` fires for
// each intermediate "what the agent is doing right now" event (see
// engine/tool_labels.py on the API side); resolves with the final
// { reply, session_id, sources, focus } payload once the turn completes.
//
// A plain axios/EventSource can't do this: EventSource is GET-only (no body,
// no custom headers), and this needs a POST body plus the portal's Bearer
// token — hence a raw fetch() reading the response body as a stream instead.
export async function postChat (message, sessionId, role, context, org, onStatus, attachmentText, attachmentName) {
  const token = store.state.USER.user.token
  const res = await fetch(`${chatbotBaseUrl()}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({
      message,
      session_id: sessionId,
      role,
      context,
      org,
      attachment_text: attachmentText || null,
      attachment_name: attachmentName || null
    })
  })

  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => ({}))
    const err = new Error(`Chatbot API HTTP ${res.status}`)
    err.response = { status: res.status, data }
    throw err
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let sep
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const evt = parseSseFrame(frame)
      if (!evt) continue
      if (evt.event === 'status') {
        onStatus(evt.data.label)
      } else if (evt.event === 'error') {
        const err = new Error(evt.data.detail || 'Chat failed')
        err.response = { data: evt.data }
        throw err
      } else if (evt.event === 'done') {
        return evt.data
      }
    }
  }
  throw new Error('Chatbot API stream ended without a result.')
}

// Extract text from a file attached to the chat, for THIS TURN's prompt
// context only — no ingestion/embedding (see POST /chat/upload). Returns
// { filename, text, truncated, chars }; the caller passes `text`/`filename`
// back into postChat() as attachmentText/attachmentName.
export function uploadChatAttachment (file) {
  const fd = new FormData()
  fd.append('file', file)
  return httpChatbot.post('/chat/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

export function resetSession (sessionId) {
  return httpChatbot.post(`/chat/${sessionId}/reset`)
}

// Roles are per context (and org), so pass the active ids (e.g. after a sport pick).
export function getRoles (context, org) {
  const params = {}
  if (context) params.context = context
  if (org) params.org = org
  return httpChatbot.get('/roles', { params })
}

// The selectable context packs (general + each active sport) for the start picker.
export function getContexts () {
  return httpChatbot.get('/contexts')
}

// Save the current conversation as reusable knowledge. visibility: 'private'
// (only this user's future sessions retrieve it) or 'shared' (everyone).
export function saveConversation (sessionId, { visibility = 'private', title = null, context = null, org = null } = {}) {
  return httpChatbot.post(`/chat/${sessionId}/save-knowledge`, { visibility, title, context, org })
}

// The current user's saved conversations (ids + titles) for an org, newest first.
export function listConversations (org) {
  return httpChatbot.get('/conversations', { params: org ? { org } : {} })
}

// One saved conversation in full (only the owner can read it).
export function getConversation (id) {
  return httpChatbot.get(`/conversations/${encodeURIComponent(id)}`)
}

export function getRadar (player, org) {
  return httpChatbot.get(`/radar/${encodeURIComponent(player)}`, { params: org ? { org } : {} })
}

export function getPlayerReports (player, org) {
  return httpChatbot.get(`/player-reports/${encodeURIComponent(player)}`, { params: org ? { org } : {} })
}

// Absolute URL to a corpus document served by the API's /docs static mount.
// A plain string (not axios) because it goes into <iframe src> / window.open.
export function docUrl (file) {
  const path = String(file).split('/').map(encodeURIComponent).join('/')
  return `${chatbotBaseUrl()}/docs/${path}`
}

// PDF URL fragment: open at the cited page, fit the page to the frame width.
export function pdfFrag (location) {
  const m = location ? String(location).match(/p\.(\d+)/) : null
  const page = m ? `page=${m[1]}&` : ''
  return `#${page}zoom=page-width`
}

// ── Admin dashboard ────────────────────────────────────────────────────────
// One context pack's OWN editable YAML (persona, roles, behaviour, guardrails).
export function getAdminContext (id) {
  return httpChatbot.get(`/admin/contexts/${encodeURIComponent(id)}`)
}

// Save edits to one context pack. `raw` is the pack's full YAML as an object.
export function saveAdminContext (id, raw) {
  return httpChatbot.put(`/admin/contexts/${encodeURIComponent(id)}`, { raw })
}

// Create a brand-new, blank context pack ("+ Add org/sport"). Returns { id, raw }.
export function createAdminContext (name, parent) {
  return httpChatbot.post('/admin/contexts', { name, parent })
}

// The knowledge corpus file tree across both data locations (base + uploads).
export function getAdminFiles () {
  return httpChatbot.get('/admin/files')
}

// Upload a knowledge document into a folder for a context. Saves immediately;
// INDEXING runs in the background (poll getUploadStatus with the returned
// `path`) — it can take minutes under the Voyage free tier's rate limit, and
// doing it inline risked the connection timing out before it finished.
// `allowPii` overrides the personal-data content guardrail — only pass true when
// the file is known to contain no real candidate data (e.g. a research paper
// whose authors' correspondence emails would otherwise trip the same check that
// exists to block Capacio's own per-candidate reports).
export function uploadKnowledgeFile (folder, context, file, allowPii) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('folder', folder || '')
  fd.append('context', context || '')
  if (allowPii) fd.append('allow_pii', 'true')
  return httpChatbot.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// Poll one upload's background indexing status by the `path` uploadKnowledgeFile returned.
export function getUploadStatus (path) {
  return httpChatbot.get('/admin/upload-status', { params: { path } })
}

// Trigger a full re-ingest of every org's corpus (long-running, background).
export function startReingest () {
  return httpChatbot.post('/admin/reingest')
}

// Poll the current/last re-ingest progress.
export function getReingestStatus () {
  return httpChatbot.get('/admin/reingest')
}

// Direct download URL for a knowledge file (auth-exempt endpoint, safe for <a href>).
export function downloadFileUrl (filePath) {
  const encoded = String(filePath).split('/').map(encodeURIComponent).join('/')
  return `${process.env.VUE_APP_CHATBOT_API_URL}/admin/download/${encoded}`
}

// Rename a file or folder in the knowledge volume. newName must be a bare name.
export function renameKnowledgeItem (filePath, newName) {
  const encoded = String(filePath).split('/').map(encodeURIComponent).join('/')
  return httpChatbot.patch(`/admin/files/${encoded}`, { new_name: newName })
}

// Move a file or folder to a different parent folder in the knowledge volume.
export function moveKnowledgeItem (source, destination) {
  return httpChatbot.post('/admin/files/move', { source, destination })
}

// Create an empty subfolder under `parent` (empty string = knowledge root).
export function createKnowledgeFolder (parent, name) {
  return httpChatbot.post('/admin/folders', { parent: parent || '', name })
}

// Permanently delete a file or folder from the knowledge volume.
export function deleteKnowledgeItem (filePath) {
  const encoded = String(filePath).split('/').map(encodeURIComponent).join('/')
  return httpChatbot.delete(`/admin/files/${encoded}`)
}

// Which top-level folders under `context`'s own corpus are excluded from
// ingestion (e.g. 'Rapporter'), and the corpus's own root path so the caller
// knows which tree nodes are toggle-eligible.
export function getSkipDirs (context) {
  return httpChatbot.get('/admin/skip-dirs', { params: { context } })
}

// Toggle one top-level folder in/out of ingestion for `context`'s org(s).
export function setSkipDir (context, folder, skip) {
  return httpChatbot.post('/admin/skip-dirs', { context, folder, skip })
}
